/**
 * Cart Reservation Timer
 * ---------------------------------------------------------------------------
 * Manages a countdown timer in the cart drawer. Persists across page refreshes
 * using localStorage. Resets when the previous timer expires and the drawer
 * is re-opened. Hides itself when the cart becomes empty.
 */

class CartReservationTimer {
  constructor() {
    this.storageKey = 'cartReservationEnd';
    this.interval = null;
    this.containers = [];
    this.displays = [];
    this.hasExpiredSession = false;

    this._updateElements();
    if (this.containers.length > 0) {
      this._init();
    }
    this._attachEvents();
  }

  _updateElements() {
    this.containers = Array.from(document.querySelectorAll('.cart-reservation-timer'));
    this.displays = Array.from(document.querySelectorAll('.cart-reservation-timer__countdown'));
    this.drawer = document.getElementById('cart-drawer');

    if (this.containers.length > 0 && !this.duration) {
      this.minutes = parseInt(this.containers[0].dataset.minutes, 10) || 5;
      this.duration = this.minutes * 60 * 1000; // ms
      this.clearOnExpire = this.containers[0].dataset.clearOnExpire === 'true';
    }

    return this.containers.length > 0 && this.displays.length > 0;
  }

  /* ----------------------------------------
     Init / Resume
  ---------------------------------------- */
  _init() {
    if (!this._updateElements()) return;

    const stored = localStorage.getItem(this.storageKey);
    const now = Date.now();

    if (stored && parseInt(stored, 10) > now) {
      // Resume from where we left off
      this.endTime = parseInt(stored, 10);
    } else {
      // Fresh start
      this.endTime = now + this.duration;
      localStorage.setItem(this.storageKey, this.endTime);
    }

    this._start();
  }

  /* ----------------------------------------
     Countdown tick
  ---------------------------------------- */
  _start() {
    clearInterval(this.interval);

    this.interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((this.endTime - Date.now()) / 1000));
      this._render(remaining);

      if (remaining <= 0) {
        clearInterval(this.interval);
        this.interval = null;
        this._expire();
      }
    }, 1000);

    // Render immediately so there's no 1-second blank
    const immediate = Math.max(0, Math.floor((this.endTime - Date.now()) / 1000));
    this._render(immediate);
  }

  /* ----------------------------------------
     Render MM:SS
  ---------------------------------------- */
  _render(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    const timeString = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    this.displays.forEach(display => {
      display.textContent = timeString;
    });
  }

  /* ----------------------------------------
     Timer expired
  ---------------------------------------- */
  _expire() {
    this._hide();
    this.hasExpiredSession = true;
    localStorage.removeItem(this.storageKey);

    if (this.clearOnExpire) {
      fetch('/cart/clear.js', { method: 'POST' })
        .then(() => location.reload())
        .catch(() => location.reload());
    }
  }

  /* ----------------------------------------
     Reset — called when drawer re-opens and
     previous timer already expired
  ---------------------------------------- */
  _reset() {
    if (this.hasExpiredSession) return;
    clearInterval(this.interval);
    this.interval = null;
    localStorage.removeItem(this.storageKey);
    if (!this._updateElements()) return;
    this.endTime = Date.now() + this.duration;
    localStorage.setItem(this.storageKey, this.endTime);
    this._start();
  }

  /* ----------------------------------------
     Hide/show the timer element
  ---------------------------------------- */
  _hide() {
    this.containers.forEach(container => container.hidden = true);
    clearInterval(this.interval);
    this.interval = null;
  }

  _show() {
    this.containers.forEach(container => container.hidden = false);
  }

  /* ----------------------------------------
     Event listeners
  ---------------------------------------- */
  _attachEvents() {
    document.addEventListener('open', (event) => {
      if (event.target && (event.target.id === 'cart-drawer' || event.target.tagName === 'CART-DRAWER')) {
        this._handleDrawerOpen();
      }
    }, true);

    document.addEventListener('cart:open', () => {
      this._handleDrawerOpen();
    });

    // Handle cart drawer refreshed events
    const handleCartRefresh = () => {
      if (this._updateElements()) {
        // A genuine cart mutation (add / remove / refresh) starts a brand-new
        // reservation, even if a previous timer had already expired this session.
        // Clearing the expired latch here lets the "expired storage" branch below
        // start a fresh countdown instead of leaving the re-rendered "--:--" stuck.
        this.hasExpiredSession = false;
        this._show();
        const stored = localStorage.getItem(this.storageKey);
        if (!stored || parseInt(stored, 10) <= Date.now()) {
          this._reset();
        } else if (!this.interval) {
          this.endTime = parseInt(stored, 10);
          this._start();
        } else {
          // If interval is already running, just render immediately
          const immediate = Math.max(0, Math.floor((this.endTime - Date.now()) / 1000));
          this._render(immediate);
        }
      }
    };

    document.addEventListener('cart-drawer:refreshed', handleCartRefresh);
    document.addEventListener('cart:refresh', handleCartRefresh);

    // Handle cart AJAX updates
    document.addEventListener('cart:updated', (event) => {
      const hasElements = this._updateElements();
      const cart = event.detail?.cart;

      if (cart) {
        if (cart.item_count === 0) {
          this._hide();
          localStorage.removeItem(this.storageKey);
        } else if (cart.item_count > 0) {
          if (hasElements && !this.hasExpiredSession) {
            this._show();
            const stored = localStorage.getItem(this.storageKey);
            if (!stored || parseInt(stored, 10) <= Date.now()) {
              this._reset();
            } else if (!this.interval) {
              this.endTime = parseInt(stored, 10);
              this._start();
            } else {
              const immediate = Math.max(0, Math.floor((this.endTime - Date.now()) / 1000));
              this._render(immediate);
            }
          }
        }
      }
    });
  }

  _handleDrawerOpen() {
    if (this.hasExpiredSession) return;
    if (this._updateElements()) {
      this._show();
      const stored = localStorage.getItem(this.storageKey);
      if (!stored || parseInt(stored, 10) <= Date.now()) {
        this._reset();
      }
    }
  }
}

// Initialise after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartReservationTimer());
} else {
  new CartReservationTimer();
}
